import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GlobeAltIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import * as wa from '../services/webAnalyticsApi';

// ---------------------------------------------------------------------------
// Website Analytics — real data from the first-party pipeline (WA Phase 3).
// Replaced the old static mock page (hardcoded notification numbers) on
// 2026-08-26. Data source: /admin/web-analytics/* over the rollup DB that
// tools/analytics/ingest_daily.py fills nightly. Taxonomy:
// docs/WEB_EVENT_DICTIONARY.md.
// ---------------------------------------------------------------------------

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Bars({ data, label, valueLabel }: {
  data: { k: string; v: number }[];
  label: string;
  valueLabel?: (v: number) => string;
}) {
  const max = Math.max(...data.map((d) => d.v), 1);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{label}</p>
      <div className="space-y-1.5">
        {data.map((d) => (
          <div key={d.k} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 w-32 truncate text-right shrink-0" title={d.k}>
              {d.k || '(none)'}
            </span>
            <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
              <div className="h-full bg-blue-500 rounded" style={{ width: `${(d.v / max) * 100}%` }} />
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-300 w-14 tabular-nums">
              {valueLabel ? valueLabel(d.v) : d.v}
            </span>
          </div>
        ))}
        {data.length === 0 && <p className="text-xs text-gray-400">No data yet.</p>}
      </div>
    </div>
  );
}

function TrendChart({ series }: { series: wa.DailyTotals[] }) {
  const max = Math.max(...series.map((d) => d.visitors_human), 1);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
        Human visitors per day <span className="text-gray-400 font-normal">(bots excluded)</span>
      </p>
      <div className="flex items-end gap-[3px] h-28">
        {series.map((d) => (
          <div key={d.day} className="flex-1 flex flex-col items-center group relative">
            <div
              className="w-full bg-blue-500 hover:bg-blue-600 rounded-t"
              style={{ height: `${Math.max(3, (d.visitors_human / max) * 100)}%` }}
            />
            <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap z-10">
              {d.day}: {d.visitors_human} humans · {d.visitors_bot} bots
            </div>
          </div>
        ))}
      </div>
      {series.length > 0 && (
        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>{series[0].day}</span>
          <span>{series[series.length - 1].day}</span>
        </div>
      )}
    </div>
  );
}

function Table({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            {head.map((h) => (
              <th key={h} className="py-2 pr-4 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50">
              {r.map((c, j) => (
                <td key={j} className={`py-1.5 pr-4 ${j === 0 ? 'text-gray-800 dark:text-gray-200' : 'text-gray-600 dark:text-gray-300 tabular-nums'}`}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td className="py-3 text-xs text-gray-400" colSpan={head.length}>No data yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const fmtMs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

export default function Analytics() {
  const [days, setDays] = useState(28);

  const { data: overview, isLoading, refetch } = useQuery({
    queryKey: ['wa-overview', days],
    queryFn: () => wa.getOverview(days).then((r) => r.data),
  });
  const { data: refs } = useQuery({
    queryKey: ['wa-bd-ref', days],
    queryFn: () => wa.getBreakdown('ref', days).then((r) => r.data),
  });
  const { data: devs } = useQuery({
    queryKey: ['wa-bd-dev', days],
    queryFn: () => wa.getBreakdown('dev', days).then((r) => r.data),
  });
  const { data: subjects } = useQuery({
    queryKey: ['wa-bd-subject', days],
    queryFn: () => wa.getBreakdown('subject', days).then((r) => r.data),
  });
  const { data: langs } = useQuery({
    queryKey: ['wa-bd-lang', days],
    queryFn: () => wa.getBreakdown('lang', days).then((r) => r.data),
  });
  const { data: pages } = useQuery({
    queryKey: ['wa-pages', days],
    queryFn: () => wa.getPages(days).then((r) => r.data),
  });
  const { data: events } = useQuery({
    queryKey: ['wa-events', days],
    queryFn: () => wa.getEvents(days).then((r) => r.data),
  });
  const { data: vitals } = useQuery({
    queryKey: ['wa-vitals', days],
    queryFn: () => wa.getVitals(days).then((r) => r.data),
  });
  const { data: bots } = useQuery({
    queryKey: ['wa-bots', days],
    queryFn: () => wa.getBots(Math.min(days, 30)).then((r) => r.data),
  });
  const { data: retention } = useQuery({
    queryKey: ['wa-retention'],
    queryFn: () => wa.getRetention().then((r) => r.data),
  });
  const { data: report } = useQuery({
    queryKey: ['wa-report'],
    queryFn: () => wa.getLatestReport().then((r) => r.data),
  });
  const { data: newsQuiz } = useQuery({
    queryKey: ['wa-news-quiz', days],
    queryFn: () => wa.getNewsQuiz(Math.min(days, 30)).then((r) => r.data),
  });

  const s = overview?.summary;
  const latestVitalsDay = vitals?.length ? vitals[vitals.length - 1].day : null;
  const latestVitals = (vitals || []).filter((v) => v.day === latestVitalsDay);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <GlobeAltIcon className="w-6 h-6 text-blue-500" />
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Website Analytics</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              clarityupsc.com · first-party pipeline, bot-filtered · aggregates only
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[7, 28, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm ${days === d
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow'}`}
            >
              {d}d
            </button>
          ))}
          <button onClick={() => refetch()} className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow" title="Refresh">
            <ArrowPathIcon className={`w-4 h-4 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={`Human visitors (${days}d)`} value={s?.visitors_human ?? '—'}
          sub={`${s?.new_visitors ?? 0} first-time`} />
        <StatCard label="From search" value={s ? `${s.search_share_pct}%` : '—'}
          sub="share of human visitors" />
        <StatCard label="Engaged (any interaction)" value={s?.engaged_visitors ?? '—'}
          sub={`single-page: ${s?.single_page_pct ?? 0}%`} />
        <StatCard label="Median dwell (latest day)" value={s ? fmtMs(s.dwell_med_ms_latest) : '—'}
          sub={`${s?.pv_human ?? 0} page views in window`} />
      </div>

      {overview && <TrendChart series={overview.series} />}

      <div className="grid md:grid-cols-2 gap-4">
        <Bars label="Acquisition (human visitors)" data={(refs || []).map((r) => ({ k: r.k, v: r.visitors }))} />
        <Bars label="Devices" data={(devs || []).map((r) => ({ k: r.k, v: r.visitors }))} />
        <Bars label="Subject demand (page views)"
          data={(subjects || []).filter((r) => r.k).slice(0, 10).map((r) => ({ k: r.k, v: r.pv }))} />
        <Bars label="Translated reading (page views by language)"
          data={(langs || []).filter((r) => r.k).map((r) => ({ k: r.k, v: r.pv }))} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Top pages ({days}d)</p>
        <Table
          head={['Path', 'Views', 'Daily uniques (sum)', 'Exit at 0%']}
          rows={(pages || []).map((p) => [
            p.path, p.pv, p.uv_daily_sum,
            p.exits ? `${Math.round((100 * p.exits0) / p.exits)}%` : '—',
          ])}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">CTA clicks</p>
          <Table
            head={['Event', 'Placement', 'Clicks', 'Visitors']}
            rows={(events?.cta || []).map((r) => [r.event, r.placement || '(none)', r.n, r.visitors])}
          />
        </div>
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Quiz funnels</p>
            <Table
              head={['Surface', 'Started', 'Completed', 'Questions', 'Accuracy']}
              rows={(events?.quiz_funnels || []).map((f) => [
                f.placement, f.started, f.completed, f.questions, `${f.accuracy_pct}%`,
              ])}
            />
            {events && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                Homepage hero quiz: {events.hero_quiz.answers} answers ·{' '}
                {events.hero_quiz.completions} completions
              </p>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Article practice widget
            </p>
            <Table
              head={['Event', 'Count']}
              rows={(events?.practice_widget || []).map((r) => [r.event, r.n])}
            />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Page speed — latest day, p75
          </p>
          <p className="text-xs text-gray-400 mb-3">Good: LCP &lt; 2.5s · INP &lt; 200ms · CLS &lt; 0.1</p>
          <Table
            head={['Page type', 'Samples', 'LCP', 'INP', 'TTFB', 'CLS']}
            rows={latestVitals.map((v) => [
              v.ptype, v.n, fmtMs(v.lcp_p75_ms), fmtMs(v.inp_p75_ms),
              fmtMs(v.ttfb_p75_ms), (v.cls_p75_milli / 1000).toFixed(3),
            ])}
          />
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Returning within 7 days (new-visitor cohorts)
          </p>
          {retention && (
            <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
              {retention.overall.return_pct}%
              <span className="text-sm font-normal text-gray-500 ml-2">
                of {retention.overall.cohort} new visitors came back
              </span>
            </p>
          )}
          <p className="text-xs text-gray-400 mt-2">
            Computed from anonymous visitor ids, capped at 90 days by the retention job.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Bars
          label="Who is indexing / scraping us (bot events)"
          data={(bots?.reasons || []).slice(0, 10).map((r) => ({ k: r.reason, v: r.events }))}
        />
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            News-quiz answers on the website (anonymous server totals)
          </p>
          <Table
            head={['Day', 'Type', 'Events', 'Questions', 'Avg score']}
            rows={(newsQuiz?.rows || []).slice(-12).map((r) => [
              r.day, r.mode || r.event_type, r.events, r.questions,
              r.avg_score_pct != null ? `${r.avg_score_pct}%` : '—',
            ])}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Latest daily report <span className="text-gray-400 font-normal">(generated 06:30 IST)</span>
        </p>
        <pre className="text-xs text-gray-600 dark:text-gray-300 overflow-x-auto whitespace-pre bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          {report?.text || 'Loading…'}
        </pre>
      </div>
    </div>
  );
}
